import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { BiCloudDownload } from 'react-icons/bi';
import { HiStatusOnline, HiOutlineStatusOffline } from 'react-icons/hi';
import { RiListSettingsLine } from 'react-icons/ri';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
// material
import { useDispatch } from 'react-redux';
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Autocomplete
} from '@mui/material';
import { getCityData } from '../../store/Slice/Global/GlobalSlice';
import { RootStyle, SearchStyle } from '../Global/Styling';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

// ----------------------------------------------------------------------

export default function CustomerToolbar({
  filterName,
  onFilterName,
  setFilterState,
  filterState,
  setFilterName,
  handleDownload,
  typeIdValue
}) {
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [showTextfield] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [assignValue, setAssignValue] = useState(' ');

  const { CityDrop, LoanTypeDrop } = useGlobalSelector();
  const dispatch = useDispatch();

  const handleGetState = useCallback(
    (optionVal) => {
      dispatch(getCityData({ stateId: optionVal }));
    },
    [dispatch]
  );

  useEffect(() => {
    handleGetState(null); // city api
  }, [handleGetState]);

  const toggleFilter = () => setIsOpenFilter((prev) => !prev);
  const closeAddDialog = () => setOpenAdd(false);
  const closeAssignDialog = () => setOpenAssign(false);

  return (
    <>
      {isOpenFilter && (
        <Box px={3} pt={2}>
          <Grid container spacing={3}>
            <Grid item xs>
              <Select
                fullWidth
                size="small"
                value={filterState.typeId ?? ' '}
                onChange={(e) => {
                  const v = e.target.value === ' ' ? null : e.target.value;
                  setFilterState((prev) => ({
                    ...prev,
                    typeId: v
                  }));
                }}
              >
                <MenuItem value=" " disabled>
                  Select Product
                </MenuItem>
                {LoanTypeDrop?.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs>
              <Autocomplete
                disablePortal
                id="combo-box-city"
                size="small"
                options={CityDrop || []}
                getOptionLabel={(option) => option?.label ?? ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={CityDrop?.find((opt) => opt.value === filterState.cityId) ?? null}
                onChange={(e, newValue) => {
                  setFilterState((prev) => ({
                    ...prev,
                    cityId: newValue ? newValue.value : null
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth placeholder="Select City" />
                )}
              />
            </Grid>
            <Grid item xs>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={filterState.isActive ?? ''}
                renderValue={(value) => {
                  if (value === '' || value === null) {
                    return <span style={{ color: '#888' }}>Select Status</span>; // placeholder style
                  }
                  return value === 'true' ? 'Active' : 'Inactive';
                }}
                onChange={(e) => {
                  setFilterState((prev) => ({
                    ...prev,
                    isActive: e.target.value === '' ? null : e.target.value
                  }));
                }}
              >
                <MenuItem value="">Select Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </Grid>
            <Grid item xs="auto">
              <Tooltip title="Reset Filter">
                <IconButton
                  sx={{ padding: '0', paddingTop: '0.4rem', paddingBottom: '0.4rem' }}
                  onClick={() => {
                    setFilterState({
                      cityId: null,
                      typeId: null,
                      isActive: null
                    });
                    setFilterName('');
                  }}
                >
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle
        sx={{
          minHeight: 45,
          borderBottom: '1px solid #eaedf1'
        }}
      >
        <Tooltip title="Download" placement="top" arrow>
          <IconButton className="squareIconButton" onClick={() => handleDownload(typeIdValue)}>
            <BiCloudDownload size={18} />
          </IconButton>
        </Tooltip>
        <Box>
          <SearchStyle
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Customer"
            classes={{
              root: 'searchHolder',
              input: 'searchInput'
            }}
            startAdornment={
              <InputAdornment position="start">
                <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
          />
          &nbsp;
          <Tooltip title="Filter list">
            <IconButton onClick={toggleFilter}>
              <Icon icon={roundFilterList} />
            </IconButton>
          </Tooltip>
          &nbsp;
        </Box>
      </RootStyle>
      <Dialog open={openAdd} onClose={closeAddDialog} fullWidth>
        <DialogTitle className="popupTitle">
          <Typography>Select Mode of Application Submission</Typography>
          <IconButton onClick={closeAddDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={3}
            display="flex"
            justifyContent="space-between"
            textAlign="center"
            mb={2}
          >
            <Grid item xs={4}>
              <Box
                className="appSubmissionLinkB"
                onClick={() => {
                  window.open(`http://losdemo.kalolytic.com/`, `_blank`);
                  setOpenAdd(false);
                }}
              >
                <HiStatusOnline className="appSubLinkIcon" />
                <Typography variant="subtitle1">Online</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                className="appSubmissionLinkB"
                onClick={() => {
                  window.open(`http://offline.kalolytic.com/`, `_blank`);
                  setOpenAdd(false);
                }}
              >
                <HiOutlineStatusOffline className="appSubLinkIcon" />
                <Typography variant="subtitle1">Offline</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                className="appSubmissionLinkB"
                onClick={() => {
                  window.open(`http://offline.kalolytic.com/`, `_blank`);
                  setOpenAdd(false);
                }}
              >
                <RiListSettingsLine className="appSubLinkIcon" />
                <Typography variant="subtitle2">Manual</Typography>
              </Box>
            </Grid>
          </Grid>
          {showTextfield && (
            <Box>
              <Box mb={2}>
                <TextField
                  fullWidth
                  inputProps={{
                    className: 'textInput'
                  }}
                  placeholder="Type master name..."
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  inputProps={{
                    className: 'textInput'
                  }}
                  placeholder="Type master name..."
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={openAssign} onClose={closeAssignDialog} fullWidth>
        <Box>
          <Box>
            <DialogTitle>27 Prospect Selected</DialogTitle>
          </Box>
        </Box>
        <DialogContent>
          <Select
            fullWidth
            inputProps={{
              className: 'textInput'
            }}
            value={assignValue}
            onChange={(e) => setAssignValue(e.target.value)}
          >
            <MenuItem value=" " disabled>
              Select
            </MenuItem>
            <MenuItem value="sbi">SBI</MenuItem>
            <MenuItem value="axisbank">AXIS BANK</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog} variant="contained">
            SUBMIT
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
